import { useEffect, useState } from 'react'
import { createFileRoute, Link, useNavigate, useRouter } from '@tanstack/react-router'
import { z } from 'zod'
import type { PreviewView, SessionRequest } from '@hedgehog/shared'
import { api } from '@/lib/api'
import { AppShell } from '@/components/app/app-shell'
import { hostOf } from '@/components/app/format'
import { LiveFrame } from '@/components/app/live-frame'
import { CompareScreens, ScreenFrame } from '@/components/app/screens'
import { SpeedScore } from '@/components/app/speed-score'
import { ErrorState, LoadingState, errorStatus } from '@/components/app/states'
import { PayPanel } from '@/components/app/pay-panel'
import { ViewportToggle, type Viewport } from '@/components/app/viewport-toggle'
import { buttonVariants } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useInterval } from '@/hooks/use-interval'

export const Route = createFileRoute('/preview/$token')({
  validateSearch: z.object({ t: z.string().optional().catch(undefined) }),
  loaderDeps: ({ search }) => ({ t: search.t }),
  loader: async ({ params, deps, abortController }) => {
    const preview = await api<PreviewView>(`/previews/${encodeURIComponent(params.token)}`, { signal: abortController.signal })
    if (deps.t) {
      // Emailed links may carry the order access token; exchanging it lets the pay button work on any device.
      const body: SessionRequest = { orderId: preview.orderId, token: deps.t }
      await api<unknown>('/session', { method: 'POST', json: body, signal: abortController.signal }).catch(() => undefined)
    }
    return preview
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData ? `Preview for ${loaderData.businessName} · Hedgehog` : 'Your preview · Hedgehog' },
      { name: 'robots', content: 'noindex, nofollow' },
    ],
  }),
  pendingComponent: () => (
    <AppShell wide>
      <LoadingState label="Loading your preview" />
    </AppShell>
  ),
  errorComponent: ({ error }) => (
    <AppShell>
      {errorStatus(error) === 410 ? (
        <div className="mx-auto flex max-w-md flex-col items-center gap-3 py-16 text-center">
          <h1 className="text-2xl font-semibold">This preview has expired</h1>
          <p className="text-muted-foreground">Previews are kept for 14 days. Run a new audit to get a fresh one.</p>
          <Link to="/" className={buttonVariants({ size: 'lg' })}>
            Start a new audit
          </Link>
        </div>
      ) : errorStatus(error) === 404 ? (
        <div className="mx-auto flex max-w-md flex-col items-center gap-3 py-16 text-center">
          <h1 className="text-2xl font-semibold">This preview link does not work</h1>
          <p className="text-muted-foreground">Check that you opened the full link from our email. Previews are deleted 30 days after they expire.</p>
          <Link to="/" className={buttonVariants({ variant: 'outline', size: 'lg' })}>
            Go to the home page
          </Link>
        </div>
      ) : (
        <ErrorState error={error} title="We could not load your preview" />
      )}
    </AppShell>
  ),
  component: PreviewPage,
})

const PAID_STATES = new Set<PreviewView['state']>([
  'paid',
  'migrating',
  'in_review',
  'needs_human',
  'awaiting_approval',
  'changes_requested',
  'awaiting_dns',
  'live',
  'handed_over',
])

function PreviewPage() {
  const preview = Route.useLoaderData()
  const router = useRouter()
  const { t } = Route.useSearch()
  const navigate = useNavigate()
  useEffect(() => {
    if (t) void navigate({ to: '.', search: (prev) => ({ ...prev, t: undefined }), replace: true })
  }, [t, navigate])
  const waiting = preview.state === 'preview_requested' || (preview.state === 'needs_human' && !preview.previewUrl)
  useInterval(() => void router.invalidate({ filter: (m) => m.routeId === Route.id }), waiting ? 15000 : null)

  return (
    <AppShell wide>
      <div className="flex flex-col gap-6">
        <header className="flex flex-col gap-1">
          <p className="text-sm text-muted-foreground">Homepage preview</p>
          <h1 className="text-3xl font-semibold tracking-tight text-balance">{preview.businessName}</h1>
          <a
            href={preview.originalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-fit text-muted-foreground underline-offset-4 hover:underline"
          >
            {hostOf(preview.originalUrl)}
          </a>
        </header>
        {waiting ? (
          <Waiting />
        ) : preview.state === 'expired' ? (
          <Ended title="This preview has expired" body="Previews are kept for 14 days. Run a new audit to get a fresh one." />
        ) : preview.state === 'declined' || preview.state === 'refunded' ? (
          <Ended title="This preview is closed" body="This order is closed. Write to us if you think this is a mistake." />
        ) : (
          <Ready preview={preview} />
        )}
      </div>
    </AppShell>
  )
}

function Waiting() {
  return (
    <div role="status" className="flex flex-col items-start gap-3 rounded-xl border border-border bg-card p-6">
      <Spinner className="size-6" aria-hidden />
      <h2 className="text-lg font-semibold">We are rebuilding your homepage</h2>
      <p className="text-muted-foreground">
        This usually takes under 30 minutes. We email you when it is ready, and this page updates by itself.
      </p>
    </div>
  )
}

function Ended({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex flex-col items-start gap-3 rounded-xl border border-border bg-card p-6">
      <h2 className="text-lg font-semibold">{title}</h2>
      <p className="text-muted-foreground">{body}</p>
      <Link to="/" className={buttonVariants({ variant: 'outline', size: 'lg' })}>
        Start a new audit
      </Link>
    </div>
  )
}

function Ready({ preview }: { preview: PreviewView }) {
  // Phones start on the mobile view; a 1440 px page scaled to a phone screen is unreadable.
  const [viewport, setViewport] = useState<Viewport>(() => (window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop'))
  const paid = PAID_STATES.has(preview.state)
  const screenshot = (
    <ScreenFrame src={preview.rebuilt[viewport]} alt={`Rebuilt homepage, ${viewport} view`} viewport={viewport} />
  )

  return (
    <>
      {paid ? (
        <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
          <p>You have paid for this rebuild. Follow the full site's progress in your order area.</p>
          <Link to="/o/$orderId" params={{ orderId: preview.orderId }} className={buttonVariants({ size: 'lg' })}>
            Go to my order
          </Link>
        </div>
      ) : (
        <PayPanel preview={preview} />
      )}

      <section aria-labelledby="compare" className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="compare" className="text-xl font-semibold">
            Before and after
          </h2>
          <ViewportToggle value={viewport} onChange={setViewport} />
        </div>
        <div className="grid grid-cols-2 gap-4 rounded-xl border border-border p-4">
          <SpeedScore score={preview.speed.original} caption="Current site, mobile speed" />
          <SpeedScore score={preview.speed.rebuilt} caption="Rebuilt site, mobile speed" />
        </div>
        <CompareScreens
          original={preview.original}
          rebuilt={preview.rebuilt}
          viewport={viewport}
          name="Homepage"
          rebuiltSlot={
            preview.previewUrl ? (
              <LiveFrame
                key={viewport}
                src={preview.previewUrl}
                width={viewport === 'desktop' ? 1440 : 390}
                title={`Rebuilt homepage, live ${viewport} view`}
                fallback={screenshot}
              />
            ) : (
              screenshot
            )
          }
        />
        {preview.previewUrl && (
          <p className="text-sm text-muted-foreground">
            The rebuilt page is live; scroll and click around.{' '}
            <a href={preview.previewUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">
              Open it in a new tab
            </a>
            . Forms are switched off in previews.
          </p>
        )}
      </section>
    </>
  )
}
